import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Modal,
  Button,
  TextField,
  Pagination,
  IconButton,
  Fade,
  InputAdornment,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";

// Modal style
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 500 },
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  p: 0,
};

// Validation
const validationSchema = Yup.object({
  question: Yup.string()
    .required("Question is required")
    .min(5, "Question must be at least 5 characters"),
  answer: Yup.string()
    .required("Answer is required")
    .min(10, "Answer must be at least 10 characters"),
});

const FaqManager = () => {
  const { id } = useParams();
  const [faqData, setFaqData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    getFaq();
  }, []);

  useEffect(() => {
    const result = faqData.filter(
      (faq) =>
        faq.faq_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.faq_answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchQuery, faqData]);

  const getFaq = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllFaqs}/${id}`
      );
      setFaqData(res.data?.faqs || []);
    } catch (error) {
      console.error("Error fetching FAQ:", error);
    }
  };

  const handleOpen = (faq = null) => {
    setEditingFaq(faq);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingFaq(null);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      question: editingFaq?.faq_title || "",
      answer: editingFaq?.faq_answer || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      editingFaq ? updateFaq(values) : addFaq(values);
    },
  });

  // ADD FAQ
  const addFaq = async (values) => {
    const payload = {
      userId: id,
      faqs: [{ faq_title: values.question, faq_answer: values.answer }],
    };

    try {
      await axiosInstance.post(apiEndPointsConfig.saveFaqDetails, payload);
      getFaq();
      handleClose();
    } catch (err) {
      console.error(err);
    }
  };

  // UPDATE FAQ
  const updateFaq = async (values) => {
    const payload = {
      userId: id,
      faqId: editingFaq._id,
      faq_title: values.question,
      faq_answer: values.answer,
    };

    try {
      await axiosInstance.put(apiEndPointsConfig.updateFaqById, payload);
      getFaq();
      handleClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Pagination
  const paginate = (data, page) =>
    data.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const paginatedData = paginate(filteredData, currentPage);

  return (
    <Box>
      {/* Search + Add FAQ */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <TextField
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#999" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "#fafafa",
              "&:hover": {
                bgcolor: "#fff",
              },
              "&.Mui-focused": {
                bgcolor: "#fff",
              },
            },
          }}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{
            background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
            borderRadius: 3,
            px: 3,
            py: 1.2,
            textTransform: "capitalize",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
            "&:hover": {
              boxShadow: "0 6px 16px rgba(99, 102, 241, 0.4)",
            },
          }}
        >
          Add FAQ
        </Button>
      </Box>

      {/* Stats Chip */}
      {filteredData.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`${filteredData.length} FAQ${filteredData.length !== 1 ? "s" : ""
              } found`}
            size="small"
            sx={{
              bgcolor: "#f0f0f0",
              color: "#666",
              fontWeight: 500,
            }}
          />
        </Box>
      )}

      {/* FAQ LIST */}
      {paginatedData.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
            bgcolor: "#fafafa",
            borderRadius: 3,
            border: "2px dashed #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#999", mb: 1, fontWeight: 500 }}
          >
            No FAQs Found
          </Typography>
          <Typography variant="body2" sx={{ color: "#bbb", mb: 3 }}>
            {searchQuery
              ? "Try adjusting your search terms"
              : "Get started by adding your first FAQ"}
          </Typography>
          {!searchQuery && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              sx={{
                borderRadius: 2,
                textTransform: "capitalize",
                borderColor: "#6366F1",
                color: "#6366F1",
              }}
            >
              Add Your First FAQ
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {paginatedData.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px !important",
                  "&:before": {
                    display: "none",
                  },
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: "#6366F1",
                        fontSize: 28,
                      }}
                    />
                  }
                  sx={{
                    minHeight: 64,
                    px: 3,
                    "&.Mui-expanded": {
                      bgcolor: "#f8f9ff",
                      borderBottom: "1px solid #e8e9ff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#6366F1",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: "#333",
                        flex: 1,
                      }}
                    >
                      {faq.faq_title}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: 3,
                    py: 3,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#666",
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {faq.faq_answer}
                  </Typography>

                  <Button
                    onClick={() => handleOpen(faq)}
                    variant="outlined"
                    startIcon={<EditIcon />}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "capitalize",
                      color: "#6366F1",
                      borderColor: "#6366F1",
                      "&:hover": {
                        bgcolor: "#f0f1ff",
                        borderColor: "#6366F1",
                      },
                    }}
                  >
                    Edit FAQ
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Pagination */}
          {filteredData.length > itemsPerPage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Pagination
                count={Math.ceil(filteredData.length / itemsPerPage)}
                page={currentPage}
                onChange={(e, val) => setCurrentPage(val)}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    background:
                      "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* ADD/EDIT MODAL */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          backdropFilter: "blur(4px)",
        }}
      >
        <Fade in={open}>
          <Box sx={style}>
            {/* Modal Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                p: 3,
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {editingFaq ? "Edit FAQ" : "Add New FAQ"}
              </Typography>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Modal Content */}
            <Box sx={{ p: 3 }}>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  label="Question"
                  name="question"
                  fullWidth
                  margin="normal"
                  value={formik.values.question}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.question && Boolean(formik.errors.question)
                  }
                  helperText={formik.touched.question && formik.errors.question}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <TextField
                  label="Answer"
                  name="answer"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={5}
                  value={formik.values.answer}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.answer && Boolean(formik.errors.answer)}
                  helperText={formik.touched.answer && formik.errors.answer}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 2,
                      textTransform: "capitalize",
                      px: 3,
                      color: "#666",
                      borderColor: "#ddd",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{
                      background:
                        "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                      borderRadius: 2,
                      textTransform: "capitalize",
                      px: 3,
                      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                    }}
                  >
                    {editingFaq ? "Save Changes" : "Add FAQ"}
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default FaqManager;
